import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { User } from 'src/app/models/user';
import { SubjectMap } from 'src/app/sessions/common/session-data-structures';
import {
  TemporaryUserMetadataEntry,
  TemporaryUserMetadataRepositoryService,
} from 'src/app/state/temporary-user-metadata-repository.service';
import { SpaceRepository } from 'src/app/state/space.repository';

@UntilDestroy()
@Injectable()
export class RaiseHandRepositoryService {
  private raiseHandSubjectMap = new SubjectMap<boolean>();
  private readonly userRaiseHandMemo = new Map<string, Observable<boolean>>();

  private prevRaisedUserIds = new Set<string>();
  private raiseHandQueueOrder: string[] = [];

  private readonly _newlyRaisedHandUserIds$ = new Subject<string[]>();
  public readonly newlyRaisedHandUserIds$ = this._newlyRaisedHandUserIds$.asObservable();

  private readonly _newlyLoweredHandUserIds$ = new Subject<string[]>();
  public readonly newlyLoweredHandUserIds$ = this._newlyLoweredHandUserIds$.asObservable();

  private _orderedRaisedHandUserIds$ = new BehaviorSubject<string[]>([]);
  public orderedRaisedHandUserIds$: Observable<string[]> = this._orderedRaisedHandUserIds$.pipe(
    distinctUntilChanged((prev, curr) => {
      if (prev.length !== curr.length) {
        return false;
      }
      return prev.every((id, index) => id === curr[index]);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this),
  );

  public orderedRaisedHandPopulatedUsers$: Observable<User[]> = this.orderedRaisedHandUserIds$.pipe(
    map((raisedHandIds) => {
      const populatedUsers = this.spaceRepository.activeSpace?.populatedUsers ?? [];
      const usersMap = new Map(populatedUsers.map((u) => [u._id, u]));
      return raisedHandIds.map((id) => usersMap.get(id)).filter((u): u is User => !!u);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this),
  );

  constructor(
    private temporaryUserMetadataRepositoryService: TemporaryUserMetadataRepositoryService,
    private spaceRepository: SpaceRepository,
  ) {
    this.setSpaceMetadataSyncSubscription();
  }

  private setSpaceMetadataSyncSubscription(): void {
    this.spaceRepository.activeSpaceCurrentRoomUid$
      .pipe(
        filter(Boolean),
        tap(() => {
          this.resetState();
        }),
        switchMap(() => this.temporaryUserMetadataRepositoryService.spaceTemporaryMetadata$),
        untilDestroyed(this),
      )
      .subscribe((metadata) => {
        this.syncFromMetadata(metadata);
      });
  }

  private resetState(): void {
    this.prevRaisedUserIds = new Set<string>();
    this.raiseHandQueueOrder = [];
    this.raiseHandSubjectMap.clear();
    this.clearUserRaiseHandMemo();
    this._orderedRaisedHandUserIds$.next([]);
  }

  private syncFromMetadata(metadata: Map<string, TemporaryUserMetadataEntry>): void {
    const nextRaisedUserIds = new Set<string>();

    const currentRoomUid = this.spaceRepository.activeSpaceCurrentRoomUid;

    for (const entry of metadata.values()) {
      if (entry.raiseHand === true && entry.userId) {
        if (entry.breakoutRoomUid === currentRoomUid) {
          nextRaisedUserIds.add(entry.userId);
        }
      }
    }

    const newRaisedHandUserIds = [...nextRaisedUserIds].filter(
      (id) => !this.prevRaisedUserIds.has(id),
    );
    const loweredHandUserIds = [...this.prevRaisedUserIds].filter(
      (id) => !nextRaisedUserIds.has(id),
    );

    // defensively made for all currently raised hand users not only new ones
    nextRaisedUserIds.forEach((userId) => {
      const subject = this.raiseHandSubjectMap.get(userId);
      subject.next(true);
    });

    for (const id of newRaisedHandUserIds) {
      this.raiseHandQueueOrder.push(id);
    }

    if (newRaisedHandUserIds.length) {
      this._newlyRaisedHandUserIds$.next(newRaisedHandUserIds);
    }

    loweredHandUserIds.forEach((userId) => {
      this.handleLowerHandUpdate(userId);
    });

    if (loweredHandUserIds.length) {
      this._newlyLoweredHandUserIds$.next(loweredHandUserIds);
    }

    this.raiseHandQueueOrder = this.raiseHandQueueOrder.filter((id) => nextRaisedUserIds.has(id));
    this.prevRaisedUserIds = new Set(nextRaisedUserIds);
    this._orderedRaisedHandUserIds$.next([...this.raiseHandQueueOrder]);
  }

  private handleLowerHandUpdate(userId: string): void {
    const subject = this.raiseHandSubjectMap.get(userId);
    subject.next(false);
  }

  public getUserRaiseHand$(userId: string): Observable<boolean> {
    let cachedObservable = this.userRaiseHandMemo.get(userId);
    if (!cachedObservable) {
      cachedObservable = this.raiseHandSubjectMap.get(userId).pipe(
        map((value) => !!value),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
        untilDestroyed(this),
      );
      this.userRaiseHandMemo.set(userId, cachedObservable);
    }
    return cachedObservable;
  }

  public getUserRaiseHandCurrentValue(userId: string): boolean {
    return !!this.raiseHandSubjectMap.get(userId).getValue();
  }

  public getCurrentRaisedHandUsersIds(): string[] {
    return this._orderedRaisedHandUserIds$.getValue();
  }

  public getOrderedRaisedHandUserIds(): Set<string> {
    return new Set(this._orderedRaisedHandUserIds$.getValue());
  }

  private clearUserRaiseHandMemo(): void {
    this.userRaiseHandMemo.clear();
  }
}
